import { HasPermission } from "@climatepartner/app-shell/auth";
import { CpServices } from "@climatepartner/utils";
import { SolutionType } from "@service/shared/enums";

/** Elevated means admin mode or CM mode. */
export const isUserElevated = (hasPermission: HasPermission) => {
  return hasPermission("cp.employee.access") || hasPermission("cphub.employee.elevated");
};

export const isTrialUser = (hasPermission: HasPermission) => {
  return hasPermission("cphub.trial.access");
};

/**
 * Account-agnostic/internal employee mode: an employee logged in to the climatepartner-internal account.
 *
 * @deprecated will be removed in future, use isUserElevated instead.
 */
export const isEmployeeInternal = (hasPermission: HasPermission) => {
  return hasPermission("cp.employee.access");
};

/**
 * Permissions read from the access token's `permissions` claim. This is not what gates access:
 * both the frontend (`hasPermission`) and the backend guards resolve Frontegg entitlements, which
 * can deny a permission the token carries (e.g. a tenant with no plan configured). Used only to
 * classify the user (employee vs customer) when picking a redirect destination. Entitlements
 * can't be trusted there because they deny everything in such tenants, including the employee
 * permissions themselves (CFC-2591). Returns [] for a missing or malformed token.
 */
export const getAccessTokenPermissions = (accessToken: string | undefined): string[] => {
  const payload = accessToken?.split(".")[1];
  if (!payload) {
    return [];
  }
  try {
    const claims: unknown = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    const permissions = (claims as { permissions?: unknown }).permissions;
    return Array.isArray(permissions) ? permissions.filter((entry): entry is string => typeof entry === "string") : [];
  } catch {
    return [];
  }
};

export const REDUCTION_MODULE_PERMISSION = "reduction-module.access";

/** Without it an account is limited to one reduction plan. */
export const hasReductionFullAccess = (hasPermission: HasPermission): boolean => {
  return hasPermission(REDUCTION_MODULE_PERMISSION);
};

export const EF_VIEW_PERMISSION = "cfc.emission-factor.view";

export const NETWORK_BUYER_V2_ACCESS_PERMISSION = "network-buyer-v2.access";

export const NETWORK_SUPPLIER_V2_ACCESS_PERMISSION = "network-supplier-v2.access";

export const canViewEmissionFactors = (hasPermission: HasPermission): boolean => {
  return isUserElevated(hasPermission) || hasPermission(EF_VIEW_PERMISSION);
};

export const RESTRICTED_DOCUMENTS_READ_PERMISSION = "documenthub.restricted-documents.read";

/** Elevated CP employees and auditors; everyone else never sees a restricted document. */
export const canReadRestrictedDocuments = (hasPermission: HasPermission): boolean => {
  return isUserElevated(hasPermission) || hasPermission(RESTRICTED_DOCUMENTS_READ_PERMISSION);
};

/** An external EF viewer is an auditor or paying customer. Drives inert EFDB links and the link-free info text. */
export const isExternalEfViewer = (hasPermission: HasPermission): boolean => {
  return hasPermission(EF_VIEW_PERMISSION) && !isUserElevated(hasPermission);
};

export const getAllowedSolutions = (
  isEmployeeInternal: boolean,
  isTrialUser: boolean,
  services: CpServices
): SolutionType[] => {
  if (isEmployeeInternal) {
    // we pass all solutions here since we don't access organisation at this stage
    // however we filter the solutions in later point according to cim
    return Object.values(SolutionType);
  }
  if (isTrialUser) {
    return [SolutionType.CCF_BASIC];
  }

  const serviceIds = Object.keys(services);

  return serviceIds.reduce((acc, serviceId) => {
    switch (serviceId) {
      case "cfc.ccf-basic":
        acc.push(SolutionType.CCF_BASIC);
        break;
      case "cfc.ccf-enterprise":
        acc.push(SolutionType.CCF_ENTERPRISE);
        break;
      case "cfc.ccf-assessment":
        acc.push(SolutionType.CCF_ASSESSMENT);
        break;
      case "cfc.ccf-employee-commuting-survey":
        acc.push(SolutionType.CCF_EMPLOYEE_COMMUTING);
        break;
    }
    return acc;
  }, [] as SolutionType[]);
};
